import React, { useMemo, useState, useEffect } from "react";
import { useGetCustomerServiceQuery } from "../../store/services/resellerApi";
import { layoutCSS } from "../common/commonStyle";

import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  Divider,
} from "@mui/material";

import Grid from "@mui/material/Grid2";
import WifiIcon from "@mui/icons-material/Wifi";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SMSComponent from "./sms/SMSComponent";
import TruecallerComponent from "./trueCaller/TruecallerComponent";
import WhatsappComponent from "../../pages/whatsappCampaign";
import RcscampaignList from "../../pages/rcsCampaignList";
import RCSComponent from "./rcs/RCSComponent";
import CreateEmailCampaign from "../../pages/EmailCampaign";

const SMSPanel = () => {
  const { data: service, isLoading } = useGetCustomerServiceQuery();

  const [serviceIndex, setServiceIndex] = useState("");
  const [sName, setSName] = useState("");
  const [service_id, setServiceId] = useState(null);

  const [currentTime, setCurrentTime] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      setCurrentTime(`${hours}:${minutes}`);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const [smsPreview, setSmsPreview] = useState({
    entity: "",
    senderId: "",
    senderIdValue: "",
    templateName: "",
    templateId: "",
    file: "",
    dbFile: "",
    additionalNumbers: "",
    message: "",
    smsType: "",
    schedule: "",
    allowLong: false,
    isFlash: false,
    charCount: 0,
    smsCount: 0,
  });

  const servicesList = useMemo(() => {
    return (service?.data || [])
      .filter((item) => item.show_service === true) // 👈 IMPORTANT
      .slice()
      .sort((a, b) => {
        if (a.service_id === 1) return -1;
        if (b.service_id === 1) return 1;
        return (a.service_id ?? 999) - (b.service_id ?? 999);
      });
  }, [service]);

  // Auto-select first available service when API data loads
  useEffect(() => {
    if (!isLoading && servicesList.length > 0) {
      const firstService = servicesList[0];

      setServiceIndex(0);
      setSName(firstService?.service?.service_name || "");
      setServiceId(firstService?.service?.service_id || null);
    }
  }, [isLoading, servicesList]);

  const handleChangeService = (e) => {
    const index = e.target.value;
    const selectedItem = servicesList[index];

    if (!selectedItem) return;

    setServiceIndex(index);
    setSName(selectedItem.service.service_name);
    setServiceId(selectedItem.service.service_id);

    // reset preview on service change
    setSmsPreview({
      entity: "",
      senderId: "",
      senderIdValue: "",
      templateName: "",
      templateId: "",
      file: "",
      dbFile: "",
      additionalNumbers: "",
      message: "",
      smsType: "",
      schedule: "",
      allowLong: false,
      isFlash: false,
      charCount: 0,
      smsCount: 0,
    });
  };

  if (!isLoading && servicesList.length === 0) {
    return (
      <Box sx={{ ...layoutCSS, textAlign: "center", mt: 10, fontSize: "1rem" }}>
        <Typography variant="h6" color="error">
          You have not been assigned any service. Please contact your
          administrator.
        </Typography>
      </Box>
    );
  }

  // Safe service type checks with null/undefined handling
  const isSMS = sName?.toLowerCase() === "sms" && service_id;
  const isRCS = sName?.toLowerCase() === "rcs" && service_id;
  const isTruecaller = sName?.toLowerCase() === "truecaller" && service_id;
  const isWhatsapp = sName?.toLowerCase() === "whatsapp" && service_id;
  const isEmail = sName?.toLowerCase() === "email" && service_id;

  // Typing animation for empty message
  const TypingIndicator = () => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#94A3B8",
            animation: "blink 1.4s infinite",
            animationDelay: `${i * 0.2}s`,
            "@keyframes blink": {
              "0%,100%": { opacity: 0.3 },
              "50%": { opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        ...layoutCSS,
        background: "rgba(255, 255, 255, 0.42)", // More transparent for better background visibility

        minHeight: "100vh",
        p: 2,
        animation: "fadeIn 0.5s ease",
        "@keyframes fadeIn": {
          from: { opacity: 0, transform: "translateY(10px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Grid container spacing={2}>
        <Grid size={isSMS ? 8 : 12}>
          {/* Section Header */}
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: 700,
              mb: 1,
              color: "#0F172A",
            }}
          >
            Campaign Panel
          </Typography>

          {/* Service dropdown - Compact, Premium Style */}
          <Box>
            <FormControl sx={{ width: "140px", zIndex: 999 }}>
              <InputLabel sx={{ fontSize: "0.9rem" }}>
                Select Service
              </InputLabel>
              <Select
                sx={{
                  height: 32,
                  fontSize: "0.75rem",
                  borderRadius: "8px",
                  background: "rgba(255, 255, 255, 0.8)",
                  transition: "all 0.2s ease",
                  "& .MuiSelect-select": {
                    py: 0.5,
                    fontSize: "0.75rem",
                  },
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(14,165,233,0.1)",
                  },
                  "&.Mui-focused": {
                    boxShadow: "0 0 0 2px rgba(14,165,233,0.2)",
                  },
                }}
                value={serviceIndex}
                label="Select Service"
                onChange={handleChangeService}
                disabled={isLoading}
              >
                {servicesList.map((item, index) => (
                  <MenuItem
                    key={index}
                    value={index}
                    sx={{ fontSize: "0.75rem" }}
                  >
                    {item?.service?.service_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Main Component - Only render when service_id is available */}
          {service_id && (
            <>
              {isSMS && (
                <SMSComponent
                  service_id={service_id}
                  onPreviewChange={setSmsPreview}
                />
              )}

              {isRCS && <RCSComponent service_id={service_id} />}
              {isTruecaller && <TruecallerComponent service_id={service_id} />}
              {isWhatsapp && <WhatsappComponent service_id={service_id} />}
              {isEmail && <CreateEmailCampaign service_id={service_id} />}
            </>
          )}
        </Grid>

        {isSMS && (
          <Grid size={4}>
            {/* Thin, realistic phone frame (no gradients, no glow) */}
            <Paper
              sx={{
                width: "260px",
                height: "480px", // Reduced from 520px
                mx: "auto",
                borderRadius: "28px",
                background: "#0F172A", // solid dark
                padding: "6px", // thin border
                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                border: "1px solid #E2E8F0",
                position: "relative",
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "scale(1.015)",
                },
              }}
            >
              {/* Inner Screen */}
              <Box
                sx={{
                  background: "#FFFFFF",
                  borderRadius: "22px",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Status Bar - Solid dark */}
                <Box
                  sx={{
                    width: "100%",
                    height: "30px",
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "#111827",
                    color: "#E5E7EB",
                  }}
                >
                  <Typography fontSize="0.7rem" opacity={0.8}>
                    {currentTime}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WifiIcon sx={{ fontSize: "14px", opacity: 0.8 }} />
                    <NetworkCellIcon sx={{ fontSize: "14px", opacity: 0.8 }} />
                    <BatteryFullIcon sx={{ fontSize: "14px", opacity: 0.8 }} />
                  </Stack>
                </Box>

                {/* Sender Header - Clean light */}
                <Box
                  sx={{
                    width: "100%",
                    height: "50px",
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid #E2E8F0",
                    background: "#F1F5F9",
                    color: "#0F172A",
                    fontWeight: 600,
                  }}
                >
                  <ArrowBackIcon fontSize="small" />
                  <Typography
                    sx={{ ml: 1, fontWeight: "bold", fontSize: "0.85rem" }}
                  >
                    {`FM-${smsPreview?.senderId?.trim() || "SENDER ID"}`}
                  </Typography>
                </Box>

                {/* Message Area */}
                <Box
                  sx={{
                    flex: 1,
                    p: 1,
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#CBD5F5",
                      borderRadius: "10px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: "#E0F2FE",
                      borderRadius: "16px",
                      px: 1.2,
                      py: 1,
                      maxWidth: "85%",
                      ml: "auto",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      fontSize: "0.75rem",
                      lineHeight: 1.4,
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        right: -6,
                        top: 10,
                        width: 0,
                        height: 0,
                        borderTop: "6px solid transparent",
                        borderBottom: "6px solid transparent",
                        borderLeft: "6px solid #E0F2FE",
                      },
                    }}
                  >
                    {smsPreview?.message?.trimEnd() ? (
                      smsPreview.message.trimEnd()
                    ) : (
                      <TypingIndicator />
                    )}
                  </Box>

                  <Divider sx={{ my: 1.5 }} />
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default SMSPanel;
